import { useState, useEffect } from 'react';
import { chapterAPI, subjectAPI, classAPI } from '../../utils/api';
import { toast } from 'react-toastify';
import {
  RiAddLine, RiEditLine, RiDeleteBinLine, RiFileListLine,
  RiCloseLine, RiLoaderLine, RiArrowDownSLine, RiArrowRightSLine,
  RiBookOpenLine, RiBookLine,
} from 'react-icons/ri';

const ChapterManagement = () => {
  const [chapters, setChapters] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [, setClasses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', subjectId: '', chapterNumber: '', description: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedClasses, setExpandedClasses] = useState({});
  const [expandedSubjects, setExpandedSubjects] = useState({});

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [chaptersRes, subjectsRes, classesRes] = await Promise.all([
        chapterAPI.getAllChapters(),
        subjectAPI.getAllSubjects(),
        classAPI.getAllClasses(),
      ]);
      setChapters(chaptersRes.data.chapters || []);
      setSubjects(subjectsRes.data.subjects || []);
      setClasses(classesRes.data.classes || []);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const organizeData = () => {
    const organized = {};
    subjects.forEach(subject => {
      const classId = subject.classId?._id || subject.classId;
      const className = subject.classId?.name || 'Unknown Class';
      if (!organized[classId]) {
        organized[classId] = { classInfo: { _id: classId, name: className }, subjects: {} };
      }
      organized[classId].subjects[subject._id] = { subjectInfo: subject, chapters: [] };
    });
    chapters.forEach(chapter => {
      const subjectId = chapter.subjectId?._id || chapter.subjectId;
      const classId = chapter.subjectId?.classId?._id || chapter.subjectId?.classId;
      if (classId && organized[classId]?.subjects[subjectId]) {
        organized[classId].subjects[subjectId].chapters.push(chapter);
      }
    });
    return Object.values(organized).sort((a, b) => a.classInfo.name.localeCompare(b.classInfo.name));
  };

  const toggleClass   = (id) => setExpandedClasses(p => ({ ...p, [id]: !p[id] }));
  const toggleSubject = (id) => setExpandedSubjects(p => ({ ...p, [id]: !p[id] }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await chapterAPI.updateChapter(editingId, formData);
        toast.success('Chapter updated');
      } else {
        await chapterAPI.createChapter(formData);
        toast.success('Chapter created');
      }
      setFormData({ name: '', subjectId: '', chapterNumber: '', description: '' });
      setShowForm(false);
      setEditingId(null);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save chapter');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure? This will delete all associated content.')) return;
    try {
      await chapterAPI.deleteChapter(id);
      toast.success('Chapter deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete chapter');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 spinner" />
    </div>
  );

  const organizedData = organizeData();

  return (
    <div className="p-6 max-w-5xl mx-auto animate-fade-in-up">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Chapters</h1>
          <p className="text-sm text-slate-500 mt-0.5">Curriculum table of contents</p>
        </div>
        <button onClick={() => { setShowForm(true); setEditingId(null); setFormData({ name: '', subjectId: '', chapterNumber: '', description: '' }); }}
          className="btn-primary whitespace-nowrap">
          <RiAddLine className="w-4 h-4" /> Add Chapter
        </button>
      </div>

      {/* Form panel */}
      {showForm && (
        <div className="card p-6 mb-6 animate-fade-in-up border-l-4 border-blue-500">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-base font-semibold text-slate-800">{editingId ? 'Edit Chapter' : 'Create New Chapter'}</h2>
            <button onClick={() => setShowForm(false)} className="btn-icon btn-ghost text-slate-400">
              <RiCloseLine className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
            <div>
              <label className="label">Subject *</label>
              <select className="input" value={formData.subjectId}
                onChange={e => setFormData({ ...formData, subjectId: e.target.value })} required>
                <option value="">Select subject</option>
                {subjects.map(s => (
                  <option key={s._id} value={s._id}>{s.classId?.name} — {s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Chapter Name *</label>
              <input className="input" type="text" placeholder="e.g. Introduction" value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })} required />
            </div>
            <div>
              <label className="label">Chapter Number</label>
              <input className="input" type="number" placeholder="e.g. 1" value={formData.chapterNumber}
                onChange={e => setFormData({ ...formData, chapterNumber: e.target.value })} min="1" />
            </div>
            <div className="sm:col-span-2">
              <label className="label">Description</label>
              <textarea className="input resize-none" placeholder="Optional description" value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })} rows={2} />
            </div>
            <div className="sm:col-span-2 flex gap-3">
              <button type="submit" disabled={saving} className="btn-primary flex-1">
                {saving ? <><RiLoaderLine className="w-4 h-4 animate-spin" /> Saving…</> : editingId ? 'Update Chapter' : 'Create Chapter'}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="btn-secondary flex-1">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Tree */}
      {organizedData.length === 0 ? (
        <div className="card p-12 text-center">
          <RiFileListLine className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No curriculum structure yet</p>
          <ol className="text-slate-400 text-sm mt-3 space-y-1">
            <li>1. Create Classes</li>
            <li>2. Create Subjects under those classes</li>
            <li>3. Add Chapters here</li>
          </ol>
        </div>
      ) : (
        <div className="space-y-3">
          {organizedData.map(classItem => (
            <div key={classItem.classInfo._id} className="card overflow-hidden">
              {/* Class row */}
              <button
                onClick={() => toggleClass(classItem.classInfo._id)}
                className="w-full flex items-center gap-3 px-5 py-4 bg-slate-800 hover:bg-slate-700 transition-colors text-left"
              >
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <RiBookOpenLine className="w-4 h-4 text-white" />
                </div>
                <span className="font-semibold text-white flex-1">{classItem.classInfo.name}</span>
                <span className="text-xs text-slate-400 mr-2">
                  {Object.keys(classItem.subjects).length} subject{Object.keys(classItem.subjects).length !== 1 ? 's' : ''}
                </span>
                {expandedClasses[classItem.classInfo._id]
                  ? <RiArrowDownSLine className="w-5 h-5 text-slate-400" />
                  : <RiArrowRightSLine className="w-5 h-5 text-slate-400" />}
              </button>

              {/* Subjects */}
              {expandedClasses[classItem.classInfo._id] && (
                <div className="divide-y divide-slate-100">
                  {Object.values(classItem.subjects).map(subjectItem => (
                    <div key={subjectItem.subjectInfo._id}>
                      {/* Subject row */}
                      <button
                        onClick={() => toggleSubject(subjectItem.subjectInfo._id)}
                        className="w-full flex items-center gap-3 px-5 py-3.5 pl-10 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                      >
                        <RiBookLine className="w-4 h-4 text-violet-500 flex-shrink-0" />
                        <span className="font-medium text-slate-700 flex-1">{subjectItem.subjectInfo.name}</span>
                        <span className="badge badge-blue mr-1">
                          {subjectItem.chapters.length} ch
                        </span>
                        {expandedSubjects[subjectItem.subjectInfo._id]
                          ? <RiArrowDownSLine className="w-4 h-4 text-slate-400" />
                          : <RiArrowRightSLine className="w-4 h-4 text-slate-400" />}
                      </button>

                      {/* Chapters */}
                      {expandedSubjects[subjectItem.subjectInfo._id] && (
                        <div className="bg-white">
                          {subjectItem.chapters.length === 0 ? (
                            <p className="pl-16 py-3 text-sm text-slate-400 italic">No chapters yet</p>
                          ) : (
                            subjectItem.chapters.map(chapter => (
                              <div key={chapter._id}
                                className="flex items-center gap-3 px-5 py-3 pl-16 border-b border-slate-50 last:border-0 hover:bg-slate-50 transition-colors group">
                                <RiFileListLine className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-slate-700">
                                    {chapter.chapterNumber ? `Ch ${chapter.chapterNumber}: ` : ''}{chapter.name}
                                  </p>
                                  {chapter.description && (
                                    <p className="text-xs text-slate-400 mt-0.5 truncate">{chapter.description}</p>
                                  )}
                                </div>
                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button
                                    onClick={() => {
                                      setEditingId(chapter._id);
                                      setFormData({ name: chapter.name, subjectId: chapter.subjectId?._id || chapter.subjectId || '', chapterNumber: chapter.chapterNumber || '', description: chapter.description || '' });
                                      setShowForm(true);
                                    }}
                                    className="btn-icon btn-ghost text-blue-600 hover:bg-blue-50"
                                  >
                                    <RiEditLine className="w-3.5 h-3.5" />
                                  </button>
                                  <button onClick={() => handleDelete(chapter._id)} className="btn-icon btn-ghost text-red-600 hover:bg-red-50">
                                    <RiDeleteBinLine className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChapterManagement;
